import type { PlayCounts, PopulationHour, PopulationSettings, PopulationView } from '@gardener/protocol';
import {
  createEffect,
  createResource,
  createSignal,
  createUniqueId,
  For,
  type JSX,
  on,
  onCleanup,
  Show,
} from 'solid-js';
import { call } from './api';
import { AnalyticsChart } from './chart';
import {
  ask,
  CARD_COLUMNS,
  count,
  optimistic,
  SETTINGS_INPUT,
  SettingsCard,
  SettingsItem,
  Switch,
  Toolbar,
  toast,
} from './ui';

/** The live numbers come again this often (ms). */
const POLL_MS = 10_000;
/** Dials being edited are charted this long after the last change (ms). */
const CHART_WAIT_MS = 300;

/** PLAY's cards, each in its chart colour (logdash's chart tokens, in their order). */
const CARDS: readonly { key: keyof PlayCounts & keyof PopulationSettings['peak']; name: string; color: string }[] = [
  { key: 'royale', name: 'Battle Royale', color: 'var(--chart-2)' },
  { key: 'zombies', name: 'Zombie Nights', color: 'var(--chart-3)' },
];
/** The rows of Now on PLAY: the cards. */
const ROWS = CARDS;

/** The stat tiles: a label, then the figure and the line under it. */
const TILES: readonly { label: string; figure: (v: PopulationView) => readonly [string, string] }[] = [
  {
    label: 'PLAY shows now',
    figure: (v) => [
      count(CARDS.reduce((n, c) => n + v.shown[c.key], 0)),
      `${count(CARDS.reduce((n, c) => n + v.real[c.key], 0))} of them really playing`,
    ],
  },
  {
    label: 'Bots',
    figure: (v) => [count(v.bots.eu + v.bots.us), `${count(v.bots.eu)} Europe · ${count(v.bots.us)} North America`],
  },
  {
    label: 'Bots in a game now',
    figure: (v) => [count(v.busy), `${count(v.league)} league games among themselves`],
  },
  {
    label: 'Battle Royale fills',
    figure: (v) => [
      `${v.settings.royaleWait[0]}–${v.settings.royaleWait[1]} s`,
      'a room’s whole wait, walked into part way',
    ],
  },
];

/** What is wrong with dials an admin typed (`PopulationBody`'s bounds), or ''. */
function problem(s: PopulationSettings): string {
  const within = (n: number, lo: number, hi: number, int = true) =>
    Number.isFinite(n) && n >= lo && n <= hi && (!int || Number.isInteger(n));
  if (!within(s.pool.eu, 0, 3000) || !within(s.pool.us, 0, 3000)) return 'A pool is 0 to 3,000 bots.';
  if (!CARDS.every((c) => within(s.peak[c.key], 0, 5000))) return 'A card’s busiest crowd is 0 to 5,000.';
  if (!within(s.quiet, 0, 1, false)) return 'The quiet share is 0 to 100 %.';
  if (!s.royaleWait.every((n) => within(n, 5, 180, false))) return 'A Battle Royale fill is 5 to 180 s.';
  if (s.royaleWait[0] > s.royaleWait[1]) return 'Battle Royale fills for at least ≤ at most.';
  return '';
}

/**
 * The population (ADR-0151): bots that are accounts like any player's, which fill a Battle Royale as it waits, and the
 * crowd PLAY shows on each card. Read at a glance (ADR-0154): what PLAY shows now
 * against who really plays, the next day of it charted (the dials being edited charted before they are saved), and the
 * dials by what they do. In force at once here and on every server within ten seconds; raising a pool makes new bots,
 * lowering one deletes none. Drawn as logdash's web analytics: its metrics card and chart, breakdown and settings cards.
 */
export function Population() {
  const [view, { refetch, mutate }] = createResource(() => call<PopulationView>('GET', '/api/admin/population'));
  const timer = setInterval(() => void refetch(), POLL_MS);
  onCleanup(() => clearInterval(timer));
  /** The dials being edited, or null: none. */
  const [draft, setDraft] = createSignal<PopulationSettings | null>(null);
  const saved = () => view()?.settings;
  const dirty = () => {
    const d = draft();
    return d !== null && JSON.stringify(d) !== JSON.stringify(saved());
  };
  const dials = () => draft() ?? saved();
  const edit = (change: (s: PopulationSettings) => PopulationSettings) => {
    const s = dials();
    if (s) setDraft(change(structuredClone(s)));
  };

  // The edited dials' day, charted before they are saved.
  const [draftDay, setDraftDay] = createSignal<PopulationHour[] | null>(null);
  createEffect(
    on(draft, (d) => {
      setDraftDay(null);
      if (!d || problem(d)) return;
      const wait = setTimeout(async () => {
        const answer = await call<{ day: PopulationHour[] }>('POST', '/api/admin/population/day', d).catch(() => null);
        if (draft() === d && answer) setDraftDay(answer.day);
      }, CHART_WAIT_MS);
      onCleanup(() => clearTimeout(wait));
    }),
  );
  const day = () => (dirty() ? draftDay() : null) ?? view()?.day ?? [];
  const wrong = () => {
    const d = draft();
    return d ? problem(d) : '';
  };

  async function turn(on: boolean) {
    const v = view();
    if (!v) return;
    const sure = await ask({
      title: on ? 'Turn the population on?' : 'Turn the population off?',
      body: on
        ? 'Its bots fill Battle Royale rooms as players, and PLAY shows the crowd again.'
        : 'Battle Royale rooms fill with the ladder’s bots, and PLAY shows only the players really on.',
      confirm: on ? 'Turn on' : 'Turn off',
      danger: !on,
    });
    if (sure === null) return;
    const settings = { ...v.settings, on };
    await optimistic(
      () => {
        mutate({ ...v, settings });
        setDraft((d) => d && { ...d, on });
        return () => mutate(v);
      },
      () => call<PopulationView>('PUT', '/api/admin/population', settings),
      (after) => {
        mutate(after);
        toast(on ? 'The population is on' : 'The population is off');
      },
    );
  }
  const switchId = createUniqueId();
  async function save() {
    const v = view();
    const d = draft();
    if (!v || !d || problem(d)) return;
    await optimistic(
      () => {
        mutate({ ...v, settings: d });
        setDraft(null);
        return () => {
          mutate(v);
          setDraft(d);
        };
      },
      () => call<PopulationView>('PUT', '/api/admin/population', d),
      (after) => {
        mutate(after);
        toast('Dials saved: every server has them within ten seconds');
      },
    );
  }

  return (
    <div class="@container flex w-full flex-col">
      <Toolbar>
        <Show when={view()}>
          {(v) => (
            <>
              <span class="bg-surface-100-bg text-fg-secondary flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[13px]">
                <span class={`size-1.5 rounded-full ${v().settings.on ? 'bg-success' : 'bg-idle'}`} />
                {v().settings.on ? 'On' : 'Off'}
              </span>
              <label
                for={switchId}
                class="text-fg-tertiary hover:text-fg-default transition-ink flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-full pr-3 pl-2.5 text-sm edge"
              >
                <Switch
                  id={switchId}
                  size="xs"
                  checked={v().settings.on}
                  label="The population"
                  onChange={(on) => void turn(on)}
                />
                Bots and the crowd
              </label>
            </>
          )}
        </Show>
      </Toolbar>

      <Show when={view.error && !view()}>
        <p class="text-error-fg edge-b px-4 py-3 text-sm" role="alert">
          {(view.error as Error).message}
        </p>
      </Show>

      <div class="flex flex-col gap-2 p-2">
        {/* logdash's MetricsCard: the tiles, the chart under them. */}
        <section
          class="bg-surface-25-bg rounded-2xl p-2 [--chart-surface:var(--surface-25-bg)]"
          aria-label="PLAY now and the next day"
        >
          <dl class="grid grid-cols-2 gap-2 @2xl:grid-cols-4">
            <For each={TILES}>
              {(tile) => {
                const figure = () => {
                  const v = view();
                  return v && tile.figure(v);
                };
                return (
                  <div class="relative flex min-w-0 flex-col gap-1.5 rounded-lg p-3">
                    <dt class="text-fg-muted flex items-center text-xs">{tile.label}</dt>
                    <dd class="font-figure text-2xl tabular-nums">
                      <Show when={figure()} fallback={<span class="bg-surface-150-bg block h-8 w-16 rounded-md" />}>
                        {figure()?.[0]}
                      </Show>
                    </dd>
                    <dd class="text-fg-muted min-h-4 text-xs tabular-nums">{figure()?.[1]}</dd>
                  </div>
                );
              }}
            </For>
          </dl>

          {/* The chart's header, as a SettingsCard's: a title, what it shows, its legend on the right (under it when narrow). */}
          <div class="flex min-h-7 flex-wrap items-start justify-between gap-x-3 gap-y-2 px-3 pt-4">
            <div class="flex min-w-0 flex-[1_1_18rem] flex-col gap-0.5">
              <h2 class="text-[13px] font-medium">The next 24 hours on PLAY</h2>
              <p class="text-fg-muted text-[13px]">
                What each card shows hour by hour with nobody really on (more when there is): busiest at 20:00 in each
                region, a little more at weekends.
                <Show when={view()?.settings.on === false}> The population is off: this is what it would show.</Show>
              </p>
            </div>
            <div class="flex min-h-5 flex-wrap items-center gap-x-3 gap-y-1">
              <Show when={dirty()}>
                <span class="ld-badge" data-variant="warning" data-size="sm">
                  Unsaved dials
                </span>
              </Show>
              <For each={CARDS}>{(c) => <Key color={c.color} label={c.name} />}</For>
            </div>
          </div>
          <div class="pt-3 pb-1.5 pl-3">
            <Show when={view()} fallback={<div class="bg-surface-150-bg h-55 rounded-xl" />}>
              <Show
                when={day().length}
                fallback={
                  <p class="text-fg-muted flex h-55 items-center justify-center text-sm">No chart from this server.</p>
                }
              >
                <AnalyticsChart
                  times={day().map((h) => Date.parse(h.at))}
                  lines={CARDS.map((c) => ({
                    label: c.name,
                    color: c.color,
                    values: day().map((h) => h.shown[c.key]),
                  }))}
                  label="What PLAY shows each hour of the next day"
                />
              </Show>
            </Show>
          </div>
        </section>

        <Show when={view()}>
          {(v) => (
            <div class="grid gap-2 @2xl:grid-cols-2">
              {/* A SettingsCard (beside the dials, its title on theirs) holding logdash's breakdown list: each bar the
                  shown count, its real part darker. One child, so no hairline under the header. */}
              <SettingsCard
                title="Now on PLAY"
                description="Each card’s count: the players really on, and the crowd the population adds over them."
                actions={
                  <span class="flex items-center gap-3 pt-1">
                    <Key color="var(--surface-200-bg)" label="Real" square />
                    <Key color="var(--surface-150-bg)" label="Added crowd" square />
                  </span>
                }
              >
                <div class="flex flex-col gap-0.5 pt-1">
                  <div class={CARD_COLUMNS}>
                    <span>Card</span>
                    <span class="flex gap-6">
                      <span class="w-16 text-right">Shown</span>
                      <span class="w-16 text-right">Real</span>
                    </span>
                  </div>
                  <ul class="flex flex-col gap-0.5">
                    <For each={ROWS}>
                      {(c) => {
                        const peak = () => Math.max(1, ...ROWS.map((r) => v().shown[r.key]));
                        const shown = () => v().shown[c.key];
                        const real = () => v().real[c.key];
                        return (
                          <li
                            class="relative flex h-8 items-center gap-3 rounded-lg px-3 text-sm"
                            title={`${real()} real, ${Math.max(0, shown() - real())} added`}
                          >
                            <span
                              class="bg-surface-150-bg absolute inset-y-0 left-0 rounded-lg"
                              style={{ width: `${Math.max(1.5, (shown() / peak()) * 100)}%` }}
                              aria-hidden="true"
                            />
                            <span
                              class="bg-surface-200-bg absolute inset-y-0 left-0 rounded-lg"
                              style={{ width: `${(Math.min(real(), shown()) / peak()) * 100}%` }}
                              aria-hidden="true"
                            />
                            <span class="relative flex min-w-0 flex-1 items-center gap-2">
                              <span class="size-2 shrink-0 rounded-full" style={{ 'background-color': c.color }} />
                              <span class="truncate">{c.name}</span>
                            </span>
                            <span class="relative flex shrink-0 gap-6 tabular-nums">
                              <span class="w-16 text-right font-medium">{count(shown())}</span>
                              <span class="text-fg-tertiary w-16 text-right">{count(real())}</span>
                            </span>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </div>
              </SettingsCard>
              <Show when={dials()}>{(s) => <Dials s={s()} edit={edit} />}</Show>
            </div>
          )}
        </Show>
      </div>

      {/* logdash's ClaimBanner: a bar held to the bottom of the panel while there is something to do. */}
      <Show when={dirty()}>
        <div class="pointer-events-none sticky bottom-0 z-10 mt-auto flex justify-center px-4 pt-8 pb-4 sm:px-8">
          <div
            class="absolute inset-0 backdrop-blur-[2px] [mask-image:linear-gradient(to_bottom,transparent,black_2rem)]"
            aria-hidden="true"
          />
          <aside
            aria-label="Unsaved dials"
            class="bg-surface-elevated-bg border-surface-elevated-border pointer-events-auto relative flex min-h-15 w-full max-w-160 flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border py-3 pr-3 pl-4 shadow-[0_2px_4px_rgba(0,0,0,0.4),0_16px_40px_-8px_rgba(0,0,0,0.9)] transition-[opacity,translate] duration-300 ease-out motion-reduce:transition-none starting:translate-y-2 starting:opacity-0"
          >
            <span class="flex h-5 shrink-0 items-center">
              <span class="ld-status-dot" data-variant={wrong() ? 'error' : 'warning'} />
            </span>
            <p class={`min-w-0 flex-[1_1_12rem] text-[15px] ${wrong() ? 'text-error-fg' : ''}`} role="status">
              {wrong() || 'Unsaved changes: the chart shows them.'}
            </p>
            <div class="ml-auto flex shrink-0 items-center gap-2">
              <button type="button" class="ld-button" data-variant="ghost" onClick={() => setDraft(null)}>
                Discard
              </button>
              <button
                type="button"
                class="ld-button"
                data-variant="primary"
                disabled={!!wrong()}
                onClick={() => void save()}
              >
                Save dials
              </button>
            </div>
          </aside>
        </div>
      </Show>
    </div>
  );
}

/** A legend's key: a colour (a dot, or a square for a bar's part) and what it stands for. */
function Key(props: { color: string; label: string; square?: boolean }) {
  return (
    <span class="text-fg-muted flex items-center gap-1.5 text-xs whitespace-nowrap">
      <span
        class={`size-2 shrink-0 ${props.square ? 'rounded-[2px]' : 'rounded-full'}`}
        style={{ 'background-color': props.color }}
      />
      {props.label}
    </span>
  );
}

/** A dial's row (logdash's `SettingsCardItem`): what it is, and its hint, name the dial on the right (`children`). */
function Item(props: { label: string; hint?: string; children: (id: string) => JSX.Element }) {
  const id = createUniqueId();
  const dial = props.children(id);
  return (
    <SettingsItem action={dial}>
      <label for={id} class="flex flex-col">
        <span>{props.label}</span>
        <Show when={props.hint}>
          <span class="text-fg-muted">{props.hint}</span>
        </Show>
      </label>
    </SettingsItem>
  );
}

/** A number typed in its unit: logdash's settings input (`SETTINGS_INPUT`, size sm), the unit inside it. */
function NumberField(props: { id: string; value: number; unit: string; set: (n: number) => void; step?: number }) {
  return (
    <span class={`${SETTINGS_INPUT} w-32`} data-size="sm">
      <input
        id={props.id}
        type="number"
        min="0"
        step={props.step ?? 1}
        value={props.value}
        class="text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        onInput={(e) => props.set(e.currentTarget.valueAsNumber)}
      />
      <span class="text-fg-muted shrink-0">{props.unit}</span>
    </span>
  );
}

/** The dials, a settings card for each thing they do. */
function Dials(props: {
  s: PopulationSettings;
  edit: (change: (s: PopulationSettings) => PopulationSettings) => void;
}) {
  const s = () => props.s;
  const set = props.edit;
  return (
    <>
      <SettingsCard
        title="The crowd on PLAY"
        description="Each card at its busiest hour (20:00 in each region), both regions together. Never fewer than the players really on."
      >
        <For each={CARDS}>
          {(c) => (
            <Item label={c.name} hint={`≈ ${count(Math.round(s().peak[c.key] * s().quiet))} at the quietest hour`}>
              {(id) => (
                <NumberField
                  id={id}
                  unit="players"
                  value={s().peak[c.key]}
                  set={(n) => set((x) => ({ ...x, peak: { ...x.peak, [c.key]: n } }))}
                />
              )}
            </Item>
          )}
        </For>
        <Item label="Quietest hour" hint="Of the busiest, at night">
          {(id) => (
            <>
              <input
                id={id}
                type="range"
                class="accent-brand w-24 cursor-pointer"
                min="0"
                max="100"
                step="5"
                value={Math.round(s().quiet * 100)}
                onInput={(e) => set((x) => ({ ...x, quiet: e.currentTarget.valueAsNumber / 100 }))}
              />
              <span class="w-10 text-right text-sm whitespace-nowrap tabular-nums">
                {Math.round(s().quiet * 100)} %
              </span>
            </>
          )}
        </Item>
      </SettingsCard>

      <SettingsCard
        title="Bots"
        description="Accounts that play like players. Raising a pool makes new bots at once; lowering one retires none."
      >
        <Item label="Europe">
          {(id) => (
            <NumberField
              id={id}
              unit="bots"
              value={s().pool.eu}
              set={(n) => set((x) => ({ ...x, pool: { ...x.pool, eu: n } }))}
            />
          )}
        </Item>
        <Item label="North America">
          {(id) => (
            <NumberField
              id={id}
              unit="bots"
              value={s().pool.us}
              set={(n) => set((x) => ({ ...x, pool: { ...x.pool, us: n } }))}
            />
          )}
        </Item>
      </SettingsCard>

      <SettingsCard
        title="Battle Royale fills"
        description="How long a Battle Royale room fills, from its first squad to its round. A party walks in part way along it, with time left to warm up."
      >
        <Item label="At least">
          {(id) => (
            <NumberField
              id={id}
              unit="s"
              value={s().royaleWait[0]}
              set={(n) => set((x) => ({ ...x, royaleWait: [n, x.royaleWait[1]] }))}
            />
          )}
        </Item>
        <Item label="At most">
          {(id) => (
            <NumberField
              id={id}
              unit="s"
              value={s().royaleWait[1]}
              set={(n) => set((x) => ({ ...x, royaleWait: [x.royaleWait[0], n] }))}
            />
          )}
        </Item>
      </SettingsCard>
    </>
  );
}
